// @flow

import * as React from "react";
import axios from "axios";
import AsyncSelect from "react-select";
import {Document, Page as PDFPage, pdfjs} from 'react-pdf';
// Every <PDFPage> here and in AllInvoices is drawn without react-pdf's text and annotation layers
// (renderTextLayer/renderAnnotationLayer={false}): the previews are pictures, and we never load react-pdf's layer CSS, so
// the text layer rendered as real, unstyled text that could spill out of the preview. The server's DRAFT watermark is text
// in the PDF, and on some window sizes it landed as a grey "DRAFT DRAFT" strip over "Generate QR Invoice" and swallowed
// every click on it (2026-10-21, a FATOORA user who couldn't generate).
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import {withRouter} from "react-router-dom";
import {isValidEmailAddress, showGenericErrorToast, SAFE_PDF_OPTIONS, isPremiumRequired, emailSendErrorMessage} from "../Utils";
import {toast} from "react-toastify";
import {FROZEN_MESSAGE, EMAIL_PREMIUM_TITLE, EMAIL_PREMIUM_MESSAGE} from "../Constants";
import PremiumFeatureModal from "./PremiumFeatureModal.react";
import LoadingScreen from "./LoadingScreen.react";
import PdfLoadingSpinner from "./PdfLoadingSpinner.react";
import ReviewPromptModal, {isEligibleForReviewPrompt} from "./ReviewPromptModal.react";
import FrozenSubscriptionBanner from "./FrozenSubscriptionBanner.react";
import {GridIcon, ChevronLeftIcon} from "./Icons.react";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
const swalInstance = withReactContent(Swal);

const QR_TYPE_LABELS = {
    "4": "Invoice's Payment Link (From QuickBooks)",
    "1": "Link (URL)",
    "2": "Data (text)",
    "3": "FATOORA (e-Invoice for KSA)",
};

const STEPS = ["What are you encoding?", "Where should it go?", "Review & generate"];

// The preview <Page> is rendered at this.state.previewRenderWidth (measured from the actual
// card so the invoice fills its full width instead of sitting at a fixed size) so we can compute
// an exact pixels-per-PDF-point scale (previewRenderWidth / page.originalWidth) - custom_position_x/y
// are stored/sent to the backend in real PDF points (see utils.py), so drag math must convert
// through this scale rather than assuming 1 screen px === 1 PDF point. This is just the initial
// value used before the container has actually been measured.
const DEFAULT_PREVIEW_WIDTH_PX = 380;
const QR_PREVIEW_ICON_PX = 60;

// The DRAFT watermark of the preview, drawn onto a rendered page canvas. Same pattern as the server's
// add_preview_watermark (utils.py): "DRAFT" rows at 30 degrees, 30pt bold, rows 60pt apart, translucent grey - in PDF
// points, times pxPerPt so it matches the page's real scale on any screen.
function drawDraftWatermark(canvas, pxPerPt) {
    const ctx = canvas.getContext("2d");
    if (!ctx) {
        return;
    }
    const fontSize = 30 * pxPerPt;
    const row = Array(12).fill("DRAFT").join("   ");
    ctx.save();
    ctx.fillStyle = "rgba(115, 120, 133, 0.22)";
    ctx.font = "bold " + fontSize + "px Helvetica, Arial, sans-serif";
    ctx.textBaseline = "alphabetic";
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(-30 * Math.PI / 180);  // canvas y points down: -30 here = the PDF's +30 (rising to the right)
    const reach = Math.sqrt(canvas.width * canvas.width + canvas.height * canvas.height);
    const rowWidth = ctx.measureText(row).width;
    let shift = 0;
    for (let y = -reach / 2; y < reach / 2; y += fontSize * 2) {
        for (let x = -reach / 2 - shift; x < reach / 2; x += rowWidth + 40 * pxPerPt) {
            ctx.fillText(row, x, y);
        }
        shift = (shift + fontSize * 2.2) % (rowWidth / 6);
    }
    ctx.restore();
}

class NewQR extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isPageLoading: true,
            premiumEmail: false,  // the plans modal for "Send" on the Free plan
            currentStep: 1,
            qrDragHintPlayed: false,  // the "you can drag this" arrows play once each time step 2 opens
            userSettings: {},
            pdfPreviewUrl: '',
            goButtonLoading: false,
            nextButtonLoading: false,
            qrData: "",
            qrDataIsInvalid: false,
            qrDataErrorMessage: "",
            qrPaymentLink: '',
            qrPaymentLinkLoading: false,  // the selected invoice's payment link is being fetched from QuickBooks
            qrPaymentLinkErrorMessage: "",
            qrPaymentLinkIsInvalid: false,
            qrUrl: '',
            qrUrlErrorMessage: "",
            qrUrlIsInvalid: false,
            pdfFileErrorMessage: "",
            qrPage: "1",
            selectedQuickBooksInvoiceId: null,
            quickBooksInvoices: [],
            resultPdfUrl: '',
            isLoading: false,
            resultQrInvoiceId: null,
            statusMessage: 'Start typing an Invoice number, example: 1001',
            downloadGeneratedQrButtonLoading: false,
            qrType: localStorage.getItem("qrDefaultType") !== null ? localStorage.getItem("qrDefaultType") : "1",
            qrCompanyName: '',
            qrCompanyNameErrorMessage: "",
            qrCompanyNameIsInvalid: false,
            qrTaxNumber: '',
            qrTaxNumberErrorMessage: "",
            qrTaxNumberIsInvalid: false,
            qrInvoiceDT: '',
            qrInvoiceDTErrorMessage: "",
            qrInvoiceDTIsInvalid: false,
            qrTaxAmount: '',
            qrTaxAmountErrorMessage: "",
            qrTaxAmountIsInvalid: false,
            qrTotalAmount: '',
            qrTotalAmountErrorMessage: "",
            qrTotalAmountIsInvalid: false,
            userCurrency: "",
            customPositionX: 0,
            customPositionY: 0,
            isDraggingQr: false,
            pdfScale: 1,
            pdfPageHeightPx: DEFAULT_PREVIEW_WIDTH_PX,
            previewRenderWidth: DEFAULT_PREVIEW_WIDTH_PX,
            qrRenderedPreviewUrl: '',
            qrPreviewRendering: false,
            qrBoxPts: null,
            qrThumbnailUrl: '',
            qrThumbnailFailed: false,
            visiblePaintedFile: '',
            previewOffsetLeft: 24,
            previewOffsetTop: 65,
            messageRecipient: '',
            messageRecipientIsInvalid: false,
            sendInvoiceButtonLoading: false,
        };

        this.processPDF = this.processPDF.bind(this);
        this.changeQRType = this.changeQRType.bind(this);
        this.changeQRPage = this.changeQRPage.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleSelectedInvoiceChanged = this.handleSelectedInvoiceChanged.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.triggerLimitReachedError = this.triggerLimitReachedError.bind(this);
        this.sendEmail = this.sendEmail.bind(this);
        this.goNext = this.goNext.bind(this);
        this.goBack = this.goBack.bind(this);
        this.startAnother = this.startAnother.bind(this);
        this.handleQrDragStart = this.handleQrDragStart.bind(this);
        this.handleQrDragMove = this.handleQrDragMove.bind(this);
        this.handleQrDragEnd = this.handleQrDragEnd.bind(this);
        this.onPdfPageLoadSuccess = this.onPdfPageLoadSuccess.bind(this);
        this.captureQrThumbnail = this.captureQrThumbnail.bind(this);
        this.onVisiblePageRendered = this.onVisiblePageRendered.bind(this);
        this.fetchQrPositionPreview = this.fetchQrPositionPreview.bind(this);
        this.measurePreviewWidth = this.measurePreviewWidth.bind(this);
        this.setPreviewDivRef = this.setPreviewDivRef.bind(this);
        // A plain {current} object rather than React.createRef() so setPreviewDivRef (a callback
        // ref, needed to hook a ResizeObserver up exactly when the node mounts) can still populate
        // it the same way - every other reader in this file keeps using `.current` as usual.
        this.previewDivRef = {current: null};
        this.dragHandleRef = React.createRef();
        this.hiddenQrPreviewRef = React.createRef();
        this.qrPreviewRequestSeq = 0;
    }

    // The invoice preview should fill the ACTUAL width of its card (which varies with viewport/
    // layout) rather than sit at a fixed size with wasted space beside it - this measures that real
    // width and feeds it to <Page> as its render width, so the PDF, the drag math, and the box
    // algebra everywhere else all stay in exact agreement with whatever size it's actually shown at.
    setPreviewDivRef(node) {
        this.previewDivRef.current = node;
        if (!node) {
            if (this.previewResizeObserver) {
                this.previewResizeObserver.disconnect();
            }
            return;
        }

        this.measurePreviewWidth();
        if (!this.previewResizeObserver) {
            this.previewResizeObserver = new ResizeObserver(this.measurePreviewWidth);
        }
        this.previewResizeObserver.disconnect();
        this.previewResizeObserver.observe(node);
    }

    measurePreviewWidth() {
        if (!this.previewDivRef.current) {
            return;
        }

        const width = Math.round(this.previewDivRef.current.offsetWidth);
        if (width > 0 && Math.abs(width - this.state.previewRenderWidth) > 1) {
            this.setState({previewRenderWidth: width});
        }
    }

    // Renders the invoice PDF with the QR actually stamped on it at the current position/type via
    // the same backend code path /process_pdf uses for the real invoice (see render_qr_pdf in
    // controllers.py), so the "Result Preview" is never just an approximation - it's guaranteed to
    // match the final generated PDF exactly. Called whenever something that affects the QR's
    // appearance settles (entering step 2, changing position/type, or finishing a drag).
    fetchQrPositionPreview(onDone) {
        if (this.state.selectedQuickBooksInvoiceId === null) {
            if (onDone) {
                onDone();
            }
            return;
        }

        this.setState({qrPreviewRendering: true});
        const requestCustomX = this.state.customPositionX;
        const requestCustomY = this.state.customPositionY;
        // Two of these requests can easily overlap (e.g. clicking Next fires one right as a
        // checkbox/radio change fires another) - network responses can then arrive OUT OF ORDER, so
        // the older, staler request could resolve after the newer one and overwrite it with wrong
        // data. This token makes sure only the response to the MOST RECENTLY SENT request is ever
        // applied; any response to a now-superseded request is silently ignored.
        const requestToken = ++this.qrPreviewRequestSeq;
        axios.post(window.SERVER_URL + '/preview_qr_pdf', {
            "qb_invoice_id": this.state.selectedQuickBooksInvoiceId.value,
            "qr_page": this.state.qrPage,
            "qr_type": this.state.qrType,
            "qr_data": this.state.qrData,
            "qr_payment_link": this.state.qrPaymentLink,
            "qr_url": this.state.qrUrl,
            "qr_company_name": this.state.qrCompanyName,
            "qr_tax_number": this.state.qrTaxNumber,
            "qr_invoice_datetime": this.state.qrInvoiceDT,
            "qr_tax_amount": this.state.qrTaxAmount,
            "qr_total_amount": this.state.qrTotalAmount,
            "custom_position_x": requestCustomX,
            "custom_position_y": requestCustomY
        }, {responseType: 'blob'}).then((res) => {
            if (requestToken !== this.qrPreviewRequestSeq) {
                // A newer request has already been sent (and possibly already resolved) since this
                // one - applying this stale response now would silently corrupt the current, correct
                // state with old data. Just drop it.
                return;
            }

            if (this.state.qrRenderedPreviewUrl) {
                URL.revokeObjectURL(this.state.qrRenderedPreviewUrl);
            }

            // The exact box (PDF points, origin bottom-left) the backend actually drew the QR into -
            // lets the drag handle be sized/positioned to match the real QR pixel-for-pixel instead
            // of guessing, since the real box size varies with the encoded data length/QR version.
            // baseCustomX/Y is the position this box was rendered AT, so the handle can keep tracking
            // the cursor live between fetches by algebra alone (see render()) instead of needing a
            // fresh network round-trip on every drag frame - that round-trip is what made dragging
            // feel laggy/jerky before.
            const qrBoxPts = {
                x: parseFloat(res.headers['x-qr-box-x']),
                y: parseFloat(res.headers['x-qr-box-y']),
                size: parseFloat(res.headers['x-qr-box-size']),
                pageWidth: parseFloat(res.headers['x-qr-page-width']),
                pageHeight: parseFloat(res.headers['x-qr-page-height']),
                baseCustomX: requestCustomX,
                baseCustomY: requestCustomY,
            };
            const validBox = !isNaN(qrBoxPts.size);
            this.setState({
                qrRenderedPreviewUrl: URL.createObjectURL(res.data),
                qrPreviewRendering: false,
                qrBoxPts: validBox ? qrBoxPts : null,
                qrThumbnailUrl: '',
                qrThumbnailFailed: false,
            }, onDone);
        }).catch((err) => {
            if (requestToken !== this.qrPreviewRequestSeq) {
                return;
            }

            // preview_qr_pdf was silently failing on real invoices in production while dev testing
            // never hit the failure - swallowing it here (console-only) meant the wizard just kept
            // showing the stale/old preview with no visible sign anything was wrong. Surface it.
            console.log(err);
            if (err.response && err.response.data instanceof Blob) {
                err.response.data.text().then((text) => {
                    let message = "Couldn't refresh the QR preview.";
                    try {
                        message = JSON.parse(text).error || message;
                    } catch (parseErr) { /* not JSON - keep generic message */ }
                    toast.error(message);
                });
            } else {
                toast.error("Couldn't refresh the QR preview. Check your connection and try again.");
            }
            // Still call onDone on failure - the caller (goNext) is waiting to advance the step, and
            // trapping the user on the current step forever would be worse than moving on with a
            // (now clearly flagged, via the toast above) stale preview.
            this.setState({qrPreviewRendering: false}, onDone);
        });
    }

    onPdfPageLoadSuccess(page) {
        this.visiblePageWidthPts = page.originalWidth;  // for drawDraftWatermark (PDF points -> canvas pixels)
        const renderWidth = this.state.previewRenderWidth || DEFAULT_PREVIEW_WIDTH_PX;
        const scale = renderWidth / page.originalWidth;
        const pageHeightPx = page.originalHeight * scale;
        const maxX = Math.max(0, (renderWidth - QR_PREVIEW_ICON_PX) / scale);
        const maxY = Math.max(0, (pageHeightPx - QR_PREVIEW_ICON_PX) / scale);
        // The drag handle is absolutely positioned within the same .app-card as the PDF preview,
        // but CSS `left`/`top: 0` on an absolutely-positioned element starts at the card's PADDING
        // edge, while the preview <div> (a normal-flow child) is inset further by the card's own
        // padding and the title block above it. Measuring the preview's real offsetLeft/offsetTop
        // (relative to that same padding edge) - instead of guessing constants - is what lets the
        // handle line up with the real QR exactly, regardless of card padding or title height.
        const previewOffsetLeft = this.previewDivRef.current ? this.previewDivRef.current.offsetLeft : this.state.previewOffsetLeft;
        const previewOffsetTop = this.previewDivRef.current ? this.previewDivRef.current.offsetTop : this.state.previewOffsetTop;
        this.setState((prevState) => ({
            pdfScale: scale,
            pdfPageHeightPx: pageHeightPx,
            previewOffsetLeft,
            previewOffsetTop,
            // A previously-saved position (from a differently-sized invoice PDF) could fall
            // outside this page's bounds - re-clamp it so the QR always starts on-page.
            customPositionX: Math.max(-(renderWidth / scale), Math.min(maxX, prevState.customPositionX)),
            customPositionY: Math.max(-pageHeightPx / scale, Math.min(maxY, prevState.customPositionY)),
        }));
    }

    // Fires once the PDF page has actually painted onto its <canvas> (later than
    // onPdfPageLoadSuccess, which only has the page's metadata). The real QR the backend drew is
    // sitting right there in the canvas pixels - cropping it out into its own little image, and
    // then painting over that spot, is what lets the drag handle show and move the ACTUAL QR
    // (instead of an empty dashed box) while dragging, without needing the backend to re-render
    // anything until the user commits the move on "Next".
    // Copies the real QR out of an OFF-SCREEN render of the QR-stamped preview into the drag
    // thumbnail. While on step 2 the visible preview shows the plain invoice (no QR in it), so the QR
    // is only ever the draggable thumbnail and the spot it started on shows the real invoice - a logo or
    // text there is simply uncovered when the QR moves away, instead of leaving a blank/white patch
    // (which both painting white on the canvas and a white overlay did). Read-only, idempotent.
    captureQrThumbnail() {
        const box = this.state.qrBoxPts;
        const canvas = this.hiddenQrPreviewRef.current && this.hiddenQrPreviewRef.current.querySelector('canvas');
        if (!box || !canvas) {
            return;
        }

        try {
            const nativeScale = canvas.width / box.pageWidth;
            const sx = box.x * nativeScale;
            const sy = (box.pageHeight - box.y - box.size) * nativeScale;
            const sSize = box.size * nativeScale;
            const crop = document.createElement('canvas');
            crop.width = sSize;
            crop.height = sSize;
            crop.getContext('2d').drawImage(canvas, sx, sy, sSize, sSize, 0, 0, sSize, sSize);
            this.setState({qrThumbnailUrl: crop.toDataURL(), qrThumbnailFailed: false});
        } catch (ex) {
            // The thumbnail is a purely cosmetic drag affordance - if cropping ever fails (e.g. a
            // browser canvas security quirk) the dashed box still works on its own.
            console.log(ex);
            this.setState({qrThumbnailFailed: true});
        }
    }

    // Which file the visible preview is showing: step 2 shows the plain invoice, every other step the
    // QR-stamped preview.
    getVisiblePreviewFile() {
        return this.state.currentStep === 2 ? this.state.pdfPreviewUrl : (this.state.qrRenderedPreviewUrl || this.state.pdfPreviewUrl);
    }

    // The visible Page has finished PAINTING (not just loaded): only now is there an invoice on screen
    // for the draggable QR to sit on.
    onVisiblePageRendered() {
        // Steps 1-2 show the plain QuickBooks invoice (the draggable QR sits on top), so DRAFT is drawn onto that canvas
        // here - a screenshot or print of the preview card is never a usable QR invoice. Step 3 shows /preview_qr_pdf's
        // PDF, which the server already watermarked with the same pattern (add_preview_watermark), so not twice.
        const visibleFile = this.getVisiblePreviewFile();
        if (!this.state.qrRenderedPreviewUrl || visibleFile !== this.state.qrRenderedPreviewUrl) {
            const canvas = this.previewDivRef.current && this.previewDivRef.current.querySelector("canvas");
            if (canvas) {
                drawDraftWatermark(canvas, canvas.width / (this.visiblePageWidthPts || 612));
            }
        }
        this.setState({visiblePaintedFile: visibleFile});
    }

    componentDidMount() {
        axios.get(window.SERVER_URL + '/get_settings').then((res) => {
            const data = res.data.result;
            this.setState({
                isPageLoading: false,
                userSettings: data,
                qrCompanyName: data.company_name,
                qrTaxNumber: data.tax_number,
                userCurrency: data.currency_type,
                customPositionX: data.custom_position_x,
                customPositionY: data.custom_position_y
            }, () => {
                // A frozen company gets the frozen screen below instead of the wizard, not the quota dialog.
                if (data.is_active !== false && data.remaining_qr_invoices_this_month < 1) {
                    this.triggerLimitReachedError();
                }
            });
        });
    }

    componentDidUpdate() {
        // A different file is now on screen (e.g. step 1's QR-stamped preview -> step 2's plain invoice): it has not
        // been painted yet, so forget that the previous one was - otherwise the QR could show over the loading spinner.
        const visibleFile = this.getVisiblePreviewFile();
        if (visibleFile !== this.lastVisibleFile) {
            this.lastVisibleFile = visibleFile;
            if (this.state.visiblePaintedFile !== visibleFile || visibleFile === '') {
                return;
            }

            this.setState({visiblePaintedFile: ''});
        }
    }

    componentWillUnmount() {
        if (this.state.qrRenderedPreviewUrl) {
            URL.revokeObjectURL(this.state.qrRenderedPreviewUrl);
        }
        if (this.previewResizeObserver) {
            this.previewResizeObserver.disconnect();
        }
        document.removeEventListener('mousemove', this.handleQrDragMove);
        document.removeEventListener('mouseup', this.handleQrDragEnd);
    }

    triggerLimitReachedError() {
        let _this = this;
        swalInstance.fire({
            icon: "error",
            title: "Plan Limit Reached",
            text: "You have generated all of your " + this.state.userSettings.plan_name + "'s " + this.state.userSettings.plan_max_qr_invoices_per_month + " QR Invoices for this month. Click OK to purchase more QR invoices.",
            confirmButtonText: "OK",
            confirmButtonColor: "#224514"
        }).then(function () {
            _this.props.history.push('/subscriptions');
        });
    }

    changeQRPage(event) {
        this.setState({qrPage: event.target.value});
    }

    changeQRType(newType) {
        this.setState({qrType: newType});
        localStorage.setItem("qrDefaultType", newType);
    }

    handleQrDragStart(e) {
        e.preventDefault();
        // Everything the drag needs is captured ONCE here and read from this snapshot for the rest
        // of the gesture - handleQrDragMove never touches this.state (a setState call is what
        // schedules a React re-render, and re-rendering this whole component on every single
        // mousemove, even throttled to one per animation frame, was still slow enough on this
        // component's size to look "jerky"). Instead the handle's DOM node is moved directly, so the
        // drag runs at native mouse-event speed with zero React involvement until the drop.
        this.dragOrigin = {
            mouseX: e.clientX,
            mouseY: e.clientY,
            startX: this.state.customPositionX,
            startY: this.state.customPositionY,
            box: this.state.qrBoxPts,
            scale: this.state.pdfScale || 1,
            pdfPageHeightPx: this.state.pdfPageHeightPx,
            renderWidth: this.state.previewRenderWidth || DEFAULT_PREVIEW_WIDTH_PX,
            offsetLeft: this.state.previewOffsetLeft,
            offsetTop: this.state.previewOffsetTop,
        };
        this.pendingCustomX = this.dragOrigin.startX;
        this.pendingCustomY = this.dragOrigin.startY;
        document.addEventListener('mousemove', this.handleQrDragMove);
        document.addEventListener('mouseup', this.handleQrDragEnd);
        this.setState({isDraggingQr: true});
    }

    handleQrDragMove(e) {
        if (!this.dragOrigin) {
            return;
        }

        // custom_position_x/y are raw PDF points added directly to the QR's padding from the
        // edge server-side (see utils.py). The preview page is rendered at a known pixel scale
        // (scale = preview px per PDF point), so mouse-drag pixels must be divided by that scale to
        // get the equivalent point delta - otherwise the point saved (and sent to the backend)
        // doesn't match where the QR visually appears in the preview, and the final PDF ends up with
        // the QR in the wrong place. The QR is anchored to the top-right corner, so moving the mouse
        // right should visually move the QR right (toward the edge), which means DEcreasing
        // custom_position_x (it's a padding value).
        const origin = this.dragOrigin;
        const deltaX = (e.clientX - origin.mouseX) / origin.scale;
        const deltaY = (e.clientY - origin.mouseY) / origin.scale;
        const newX = origin.startX - deltaX;
        const newY = origin.startY + deltaY;

        // Clamp using the REAL QR box size from the last render (it varies with the encoded data's
        // length/QR version - a fixed guess here previously let long-data QRs be dragged past the
        // page edge even though the handle looked on-page). We don't know the backend's base
        // padding directly, but we can back it out from the box snapshot taken at drag-start
        // (box.x = page - base - width - customPosition), then re-solve for the customPosition that keeps the box within [0, page - width].
        const box = origin.box;
        let maxX = Math.max(0, (origin.renderWidth - QR_PREVIEW_ICON_PX) / origin.scale);
        let maxY = Math.max(0, (origin.pdfPageHeightPx - QR_PREVIEW_ICON_PX) / origin.scale);
        let minX = 0;
        let minY = 0;
        // Only trust the exact box if every field we need from it is actually a valid number -
        // an incomplete/stale box here must never be allowed to produce NaN, which would silently
        // freeze the drag (NaN propagates through Math.min/max/round).
        const boxIsUsable = box && [box.x, box.y, box.size, box.pageWidth].every((n) => typeof n === "number" && !isNaN(n));
        if (boxIsUsable) {
            // box.x = pageWidth - size - padding (padding grows -> box.x shrinks toward 0), so the
            // largest custom position that keeps the QR on the page is the one at which the box's left
            // edge reaches 0 (and likewise its bottom edge for Y).
            // box.x / box.y describe the QR where it was RENDERED (at baseCustomX/Y), not where the
            // current drag started: after a first drag (no re-fetch) startX has moved away from
            // baseCustomX, and measuring the remaining room from startX let the next drag overshoot
            // the page edge by exactly that difference, again and again.
            maxX = box.baseCustomX + box.x;
            maxY = box.baseCustomY + box.y;
            // The other direction: the QR starts a margin away from the right/top edge, and the user must
            // be able to move it all the way to those edges too. Custom position can go negative for that:
            // the QR's right edge reaches the page's right edge (box.x + size + (base - X) = pageWidth).
            minX = box.baseCustomX - (box.pageWidth - (box.x + box.size));
            minY = box.baseCustomY - (box.pageHeight - (box.y + box.size));
        }

        const clampedX = Math.round(Math.max(minX, Math.min(maxX, newX)));
        const clampedY = Math.round(Math.max(minY, Math.min(maxY, newY)));
        this.pendingCustomX = isNaN(clampedX) ? origin.startX : clampedX;
        this.pendingCustomY = isNaN(clampedY) ? origin.startY : clampedY;

        // Move the DOM directly - no setState, no re-render, so this runs at native mouse-event
        // speed no matter how large/slow the rest of the component's render tree is.
        if (boxIsUsable && this.dragHandleRef.current) {
            const dx = this.pendingCustomX - box.baseCustomX;
            const dy = this.pendingCustomY - box.baseCustomY;
            const liveX = box.x - dx;
            const liveY = box.y - dy;
            this.dragHandleRef.current.style.left = (origin.offsetLeft + liveX * origin.scale) + "px";
            this.dragHandleRef.current.style.top = (origin.offsetTop + (box.pageHeight - liveY - box.size) * origin.scale) + "px";
        }
    }

    handleQrDragEnd() {
        this.dragOrigin = null;
        document.removeEventListener('mousemove', this.handleQrDragMove);
        document.removeEventListener('mouseup', this.handleQrDragEnd);
        // The one and only setState for the whole gesture - commits wherever the drag ended up, so
        // the handle and everything else derived from state re-sync in a single re-render. Deliberately NOT re-fetching the real preview here - that only needs to catch
        // up once, right before the user moves on (see goNext) - doing it on every drop added a
        // network round-trip's worth of lag/flicker to the drag.
        this.setState({
            isDraggingQr: false,
            customPositionX: this.pendingCustomX,
            customPositionY: this.pendingCustomY,
        });
    }

    handleChange(e) {
        let value = e.target.value;
        if (e.target.name === "messageRecipient" && value.length > 200) {
            return;
        }

        this.setState({[e.target.name]: value});
    }

    validateStep1() {
        if (this.state.selectedQuickBooksInvoiceId === null) {
            this.setState({pdfFileErrorMessage: "Please choose an invoice"});
            return false;
        }
        this.setState({pdfFileErrorMessage: ""});

        if (this.state.qrType === "1" && this.state.qrUrl.length === 0) {
            this.setState({qrUrlIsInvalid: true, qrUrlErrorMessage: "Please add a link (URL)"});
            return false;
        }
        this.setState({qrUrlIsInvalid: false, qrUrlErrorMessage: ""});

        if (this.state.qrType === "2" && this.state.qrData.length === 0) {
            this.setState({qrDataIsInvalid: true, qrDataErrorMessage: "Please add data"});
            return false;
        }
        this.setState({qrDataIsInvalid: false, qrDataErrorMessage: ""});

        if (this.state.qrType === "4" && this.state.qrPaymentLinkLoading) {
            this.setState({qrPaymentLinkIsInvalid: true, qrPaymentLinkErrorMessage: "Please wait for the invoice's payment link to load"});
            return false;
        }
        if (this.state.qrType === "4" && this.state.qrPaymentLink.length === 0) {
            this.setState({qrPaymentLinkIsInvalid: true, qrPaymentLinkErrorMessage: "Please add a payment link"});
            return false;
        }
        this.setState({qrPaymentLinkIsInvalid: false, qrPaymentLinkErrorMessage: ""});

        if (this.state.qrType === "3") {
            if (this.state.qrCompanyName.length === 0) {
                this.setState({qrCompanyNameIsInvalid: true, qrCompanyNameErrorMessage: "Please add your company/seller name"});
                return false;
            }
            if (this.state.qrTaxNumber.length === 0) {
                this.setState({qrTaxNumberIsInvalid: true, qrTaxNumberErrorMessage: "Please add your tax number"});
                return false;
            }
            if (this.state.qrInvoiceDT.length === 0) {
                this.setState({qrInvoiceDTIsInvalid: true, qrInvoiceDTErrorMessage: "Please add a date and time for the invoice"});
                return false;
            }
            if (this.state.qrTaxAmount.length === 0) {
                this.setState({qrTaxAmountIsInvalid: true, qrTaxAmountErrorMessage: "Please add a tax amount"});
                return false;
            }
            if (this.state.qrTotalAmount.length === 0) {
                this.setState({qrTotalAmountIsInvalid: true, qrTotalAmountErrorMessage: "Please add a total amount"});
                return false;
            }
            this.setState({qrCompanyNameIsInvalid: false, qrTaxNumberIsInvalid: false, qrInvoiceDTIsInvalid: false, qrTaxAmountIsInvalid: false, qrTotalAmountIsInvalid: false});
        }

        return true;
    }

    goNext() {
        if (this.state.currentStep === 1 && !this.validateStep1()) {
            return;
        }

        const nextStep = this.state.currentStep + 1;
        if (nextStep === 2 || nextStep === 3) {
            // Both the first arrival at step 2 and leaving it for step 3 show the "Result Preview"
            // panel, and it must already have the real QR-stamped PDF (and the box info the drag
            // handle needs) BEFORE that panel appears - switching the step first and fetching after
            // is what caused the panel to flash empty/stale (just the invoice with no QR, or a
            // dashed drag box floating over nothing) for a moment. So wait for the fetch to actually
            // finish before switching steps, with a loading state on the button standing in for
            // however long that takes.
            this.setState({nextButtonLoading: true});
            this.fetchQrPositionPreview(() => {
                this.setState({currentStep: nextStep, nextButtonLoading: false, qrDragHintPlayed: nextStep !== 2});
            });
            return;
        }

        this.setState({currentStep: nextStep});
    }

    goBack() {
        const previousStep = Math.max(1, this.state.currentStep - 1);
        this.setState({currentStep: previousStep, qrDragHintPlayed: previousStep !== 2});
    }

    processPDF() {
        if (!this.validateStep1()) {
            this.setState({currentStep: 1});
            return;
        }

        this.setState({goButtonLoading: true});
        axios.post(window.SERVER_URL + '/process_pdf', {
            "qb_invoice_id": this.state.selectedQuickBooksInvoiceId.value,
            "qr_page": this.state.qrPage,
            "qr_type": this.state.qrType,
            "qr_data": this.state.qrData,
            "qr_payment_link": this.state.qrPaymentLink,
            "qr_url": this.state.qrUrl,
            "qr_company_name": this.state.qrCompanyName,
            "qr_tax_number": this.state.qrTaxNumber,
            "qr_invoice_datetime": this.state.qrInvoiceDT,
            "qr_tax_amount": this.state.qrTaxAmount,
            "qr_total_amount": this.state.qrTotalAmount,
            "invoice_title": this.state.selectedQuickBooksInvoiceId.label,
            "custom_position_x": this.state.customPositionX,
            "custom_position_y": this.state.customPositionY
        }).then((res) => {
            this.setState({
                goButtonLoading: false,
                currentStep: 4,
                resultPdfUrl: res.data.generated_pdf_url,
                resultQrInvoiceId: res.data.generated_invoice_id,
                messageRecipient: this.state.selectedQuickBooksInvoiceId.invoiceEmail || '',
            });
            if (localStorage.getItem("firstInvoiceCreated") === null) {
                window.$crisp.push(["set", "session:event", [[["qrify:firstinvoicecreated", {}, "red"]]]]);
                localStorage.setItem("firstInvoiceCreated", "true");
            }
        }).catch((err) => {
            this.setState({goButtonLoading: false});
            const errorCode = err.response && err.response.data && err.response.data.error;
            // Known error codes get the UI's own wording; the server's text is never shown as-is.
            if (errorCode === "quickbooks_reconnect_required") {
                return; // the app-wide axios interceptor (SiteWrapper) already shows the "reconnect QuickBooks" toast
            } else if (errorCode === "plan_limit_exceeded") {
                toast.error("You've used all the QR invoices included in your plan for this billing period. Upgrade your plan or wait until it renews.");
            } else if (errorCode === "company_inactive") {
                toast.error(FROZEN_MESSAGE);
            } else {
                showGenericErrorToast();
            }
        });
    }

    sendEmail() {
        let _this = this;
        // Emailing QR invoices is a paid-plan feature: on the Free plan "Send" offers the plans instead.
        if ((this.state.userSettings.plan_id || 1) <= 1) {
            this.setState({premiumEmail: true});
            return;
        }
        this.setState({sendInvoiceButtonLoading: true, messageRecipientIsInvalid: false});
        const messageRecipient = this.state.messageRecipient.trim();
        if (messageRecipient.length < 1 || isValidEmailAddress(messageRecipient) === false) {
            this.setState({messageRecipientIsInvalid: true, sendInvoiceButtonLoading: false});
            return;
        }

        const invoiceLabel = this.state.selectedQuickBooksInvoiceId.label;
        axios.post(window.SERVER_URL + '/send_user_qr_invoice_as_email', {
            qr_invoice_id: this.state.resultQrInvoiceId,
            message_recipient: messageRecipient,
            message_subject: invoiceLabel.substring(0, invoiceLabel.indexOf('(') - 1) + " from " + this.state.userSettings.company_name,
            message_content: "Hello,\n\nWe appreciate your business. Please find your invoice details here. Feel free to contact us if you have any questions.\n\nHave a great day!\n" + this.state.userSettings.company_name,
        }).then(function (response) {
            if (response.status === 200) {
                toast.success("Invoice sent successfully");
                _this.setState({sendInvoiceButtonLoading: false});
            }
        }).catch(error => {
            console.log(error);
            _this.setState({sendInvoiceButtonLoading: false});
            if (isPremiumRequired(error)) {
                // The plan changed to Free since this page loaded: offer the plans rather than an error.
                _this.setState({premiumEmail: true});
            } else {
                toast.error(emailSendErrorMessage(error));
            }
        });
    }

    // Bought a plan from the premium modal on the final step: reload the settings (now paid) and stay on the card with
    // the recipient still filled in, so "Send" just works.
    emailAfterUpgrade(plan) {
        axios.get(window.SERVER_URL + '/get_settings').then((res) => {
            this.setState({userSettings: res.data.result, premiumEmail: false});
            toast.success("You're on " + ((plan && plan.plan_name) || "your new plan") + "! You can now email QR invoices.");
        }).catch(() => {
            this.setState({premiumEmail: false});
            showGenericErrorToast();
        });
    }

    handleSelectedInvoiceChanged(e) {
        // The payment link comes from QuickBooks and can take a few seconds: the field is cleared and disabled
        // ("Loading...") until it arrives, instead of the old link staying editable and then being replaced under the
        // user's cursor. Only the latest invoice's answer is applied (picking another invoice meanwhile wins).
        const requestId = (this.paymentLinkRequestId || 0) + 1;
        this.paymentLinkRequestId = requestId;
        if (e !== null) {
            this.setState({qrPaymentLink: "", qrPaymentLinkLoading: true, qrPaymentLinkIsInvalid: false, qrPaymentLinkErrorMessage: ""});
            axios.get(window.SERVER_URL + '/invoice/' + e.value).then((res) => {
                if (this.paymentLinkRequestId === requestId) {
                    this.setState({qrPaymentLink: (res.data.result && res.data.result['payment_link']) || "", qrPaymentLinkLoading: false,
                                   qrPaymentLinkIsInvalid: false, qrPaymentLinkErrorMessage: ""});
                }
            }).catch((err) => {
                console.log(err);
                if (this.paymentLinkRequestId === requestId) {
                    this.setState({qrPaymentLinkLoading: false});
                }
            });
        } else {
            this.setState({qrPaymentLinkLoading: false});
        }

        if (this.state.qrRenderedPreviewUrl) {
            URL.revokeObjectURL(this.state.qrRenderedPreviewUrl);
        }

        this.setState({
            selectedQuickBooksInvoiceId: e,
            pdfPreviewUrl: (e !== null) ? window.SERVER_URL + '/mirror_user_pdf/' + e.value + '.pdf' : '',
            qrRenderedPreviewUrl: '',
            qrBoxPts: null,
            qrThumbnailUrl: '',
            qrThumbnailFailed: false,
            qrTotalAmount: (e !== null) ? e.totalAmount : '',
            qrTaxAmount: (e !== null) ? e.taxAmount : '',
            qrInvoiceDT: (e !== null) ? e.invoiceDate : '',
            pdfFileErrorMessage: ""
        });
    }

    onKeyDown(e) {
        if (e.target.value === "") {
            return;
        }
        e.target.value = e.target.value.replace(/[^0-9]/ig, '')
        let _this = this;
        this.setState({statusMessage: "Searching...", isLoading: true});
        axios.post(window.SERVER_URL + '/search_invoices', {
            text: e.target.value
        }).then(function (response) {
            _this.setState({quickBooksInvoices: response.data.quickbooks_invoices, isLoading: false, statusMessage: 'No Results'})
        });
    }

    downloadGeneratedQrInvoice(resultQrInvoiceId, invoiceTitle) {
        this.setState({downloadGeneratedQrButtonLoading: true});
        axios.get(window.SERVER_URL + '/download_user_qr_pdf/' + resultQrInvoiceId + '.pdf').then((res) => {
            const link = document.createElement('a');
            link.href = res.data;
            link.setAttribute('target', '_blank');
            link.setAttribute('download', invoiceTitle + " (Scan2Pay).pdf");
            document.body.appendChild(link);
            link.click();
            link.remove();
            this.setState({downloadGeneratedQrButtonLoading: false});
        }).catch(() => {
            showGenericErrorToast();
            this.setState({downloadGeneratedQrButtonLoading: false});
        });
    }

    startAnother() {
        if (this.state.qrRenderedPreviewUrl) {
            URL.revokeObjectURL(this.state.qrRenderedPreviewUrl);
        }

        this.setState({
            currentStep: 1,
            selectedQuickBooksInvoiceId: null,
            qrData: "",
            qrUrl: "",
            qrPaymentLink: "",
            qrPaymentLinkLoading: false,
            pdfPreviewUrl: "",
            qrRenderedPreviewUrl: "",
            qrBoxPts: null,
            qrThumbnailUrl: "",
            qrThumbnailFailed: false,
            resultPdfUrl: "",
            resultQrInvoiceId: null,
        });
        axios.get(window.SERVER_URL + '/get_settings').then((res) => {
            if (res.data.result.remaining_qr_invoices_this_month < 1) {
                this.triggerLimitReachedError();
            }
        });
    }

    renderStepper() {
        return (
            <div style={{display: "flex", alignItems: "center", marginBottom: "32px"}}>
                {STEPS.map((label, index) => {
                    const stepNumber = index + 1;
                    const isComplete = this.state.currentStep > stepNumber;
                    const isCurrent = this.state.currentStep === stepNumber;
                    return (
                        <React.Fragment key={label}>
                            <div style={{display: "flex", alignItems: "center", gap: "10px"}}>
                                <div style={{
                                    width: "28px", height: "28px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                                    border: (isComplete || isCurrent) ? "2px solid #224514" : "2px solid #d5dbe3",
                                    background: isComplete ? "#224514" : "#fff",
                                    color: isComplete ? "#fff" : (isCurrent ? "#224514" : "#8892a0"),
                                    fontWeight: 700, fontSize: "0.85rem", flexShrink: 0
                                }}>{isComplete ? "✓" : stepNumber}</div>
                                <span className="wizard-step-label" style={{fontWeight: isCurrent ? 700 : 500, color: (isComplete || isCurrent) ? "#232b3d" : "#8892a0"}}>{label}</span>
                            </div>
                            {stepNumber < STEPS.length ? <div style={{flex: 1, height: "2px", background: isComplete ? "#224514" : "#e6e9ed", margin: "0 16px"}}></div> : null}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    }

    renderQrTypeGrid() {
        const options = ["4", "1", "2", "3"];
        return (
            <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px"}}>
                {options.map((typeValue) => (
                    <button key={typeValue} type="button"
                            className={"btn " + (this.state.qrType === typeValue ? "btn-primary" : "btn-outline")}
                            style={{justifyContent: "center", textAlign: "center", padding: "14px"}}
                            onClick={() => this.changeQRType(typeValue)}>
                        {QR_TYPE_LABELS[typeValue]}
                    </button>
                ))}
            </div>
        );
    }

    renderStep1() {
        return (
            <div>
                <h2>What are you encoding?</h2>
                <p style={{color: "#8892a0", marginTop: "-8px"}}>Choose the invoice and pick what kind of QR code to generate for it.</p>

                <div className="app-form-group">
                    <label className="app-form-label">Choose an Invoice:</label>
                    <AsyncSelect
                        cacheOptions
                        options={this.state.quickBooksInvoices}
                        onChange={this.handleSelectedInvoiceChanged}
                        onKeyDown={this.onKeyDown}
                        value={this.state.selectedQuickBooksInvoiceId}
                        placeholder="Search for an invoice by its number..."
                        noOptionsMessage={() => this.state.statusMessage}
                        isClearable={true}
                        isLoading={this.state.isLoading}
                    />
                    {this.state.pdfFileErrorMessage ? <span className="app-form-error">{this.state.pdfFileErrorMessage}</span> : null}
                </div>

                <label className="app-form-label">QR Type:</label>
                {this.renderQrTypeGrid()}

                {this.state.qrType === "4" ? (
                    <div className="app-form-group">
                        <input className={"app-form-input" + (this.state.qrPaymentLinkIsInvalid ? " invalid" : "")} name="qrPaymentLink" maxLength="200"
                               placeholder={this.state.qrPaymentLinkLoading ? "Loading..." : "https://connect.intuit.com/t/..."}
                               disabled={this.state.qrPaymentLinkLoading} aria-busy={this.state.qrPaymentLinkLoading}
                               onChange={this.handleChange} value={this.state.qrPaymentLinkLoading ? "" : this.state.qrPaymentLink} />
                        {this.state.qrPaymentLinkErrorMessage ? <span className="app-form-error">{this.state.qrPaymentLinkErrorMessage}</span> : null}
                    </div>
                ) : null}

                {this.state.qrType === "1" ? (
                    <div className="app-form-group">
                        <input className={"app-form-input" + (this.state.qrUrlIsInvalid ? " invalid" : "")} name="qrUrl" maxLength="200"
                               placeholder="https://example.com/?invoice=123456" onChange={this.handleChange} value={this.state.qrUrl} />
                        {this.state.qrUrlErrorMessage ? <span className="app-form-error">{this.state.qrUrlErrorMessage}</span> : null}
                    </div>
                ) : null}

                {this.state.qrType === "2" ? (
                    <div className="app-form-group">
                        <textarea className={"app-form-textarea" + (this.state.qrDataIsInvalid ? " invalid" : "")} maxLength={200} name="qrData" rows="2"
                                  placeholder="Enter textual data here..." onChange={this.handleChange} value={this.state.qrData} />
                        {this.state.qrDataErrorMessage ? <span className="app-form-error">{this.state.qrDataErrorMessage}</span> : null}
                    </div>
                ) : null}

                {this.state.qrType === "3" ? (
                    <div>
                        <div style={{background: "#eaf3fb", border: "1px solid #cfe3f5", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px", fontSize: "0.85rem"}}>
                            ⓘ FATOORA QR codes <b>should only be scanned</b> with the official ZATCA app: &nbsp;
                            <a href="https://apps.apple.com/us/app/zatca/id1517289036" target="_blank" rel="noopener noreferrer">iOS</a> / <a href="https://play.google.com/store/apps/details?id=com.gazt.egazt" target="_blank" rel="noopener noreferrer">Android</a>.
                        </div>
                        <div className="app-form-group">
                            <label className="app-form-label">Company/Seller Name:</label>
                            <input className={"app-form-input" + (this.state.qrCompanyNameIsInvalid ? " invalid" : "")} name="qrCompanyName" maxLength="100"
                                   placeholder="Example Company" onChange={this.handleChange} value={this.state.qrCompanyName} />
                            {this.state.qrCompanyNameErrorMessage ? <span className="app-form-error">{this.state.qrCompanyNameErrorMessage}</span> : null}
                        </div>
                        <div className="app-form-group">
                            <label className="app-form-label">Tax/VAT Number:</label>
                            <input className={"app-form-input" + (this.state.qrTaxNumberIsInvalid ? " invalid" : "")} name="qrTaxNumber" maxLength="200"
                                   placeholder="1234567891234" onChange={this.handleChange} value={this.state.qrTaxNumber} />
                            {this.state.qrTaxNumberErrorMessage ? <span className="app-form-error">{this.state.qrTaxNumberErrorMessage}</span> : null}
                        </div>
                        <div className="app-form-group">
                            <label className="app-form-label">Date and Time:</label>
                            <input className={"app-form-input" + (this.state.qrInvoiceDTIsInvalid ? " invalid" : "")} name="qrInvoiceDT"
                                   placeholder="dd/mm/yyyy HH:MM" onChange={this.handleChange} value={this.state.qrInvoiceDT} />
                            {this.state.qrInvoiceDTErrorMessage ? <span className="app-form-error">{this.state.qrInvoiceDTErrorMessage}</span> : null}
                        </div>
                        <div className="app-form-group">
                            <label className="app-form-label">Tax Amount ({this.state.userCurrency}):</label>
                            <input className={"app-form-input" + (this.state.qrTaxAmountIsInvalid ? " invalid" : "")}
                                   name="qrTaxAmount" maxLength="12" step="0.01" type="number" placeholder="2.01" onChange={this.handleChange} value={this.state.qrTaxAmount} />
                            {this.state.qrTaxAmountErrorMessage ? <span className="app-form-error">{this.state.qrTaxAmountErrorMessage}</span> : null}
                        </div>
                        <div className="app-form-group">
                            <label className="app-form-label">Total Amount ({this.state.userCurrency}):</label>
                            <input className={"app-form-input" + (this.state.qrTotalAmountIsInvalid ? " invalid" : "")}
                                   name="qrTotalAmount" maxLength="12" step="0.01" type="number" placeholder="13.37" onChange={this.handleChange} value={this.state.qrTotalAmount} />
                            {this.state.qrTotalAmountErrorMessage ? <span className="app-form-error">{this.state.qrTotalAmountErrorMessage}</span> : null}
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }

    renderStep2() {
        return (
            <div>
                <h2>Where should it go?</h2>
                <div style={{background: "#eaf3fb", border: "1px solid #cfe3f5", borderRadius: "8px", padding: "12px 16px", fontSize: "0.85rem", marginTop: "-8px", marginBottom: "20px"}}>
                    ⓘ Drag the QR code directly on the preview (right side) to reposition it on the invoice.
                </div>

                <label className="app-form-label">Add QR To:</label>
                <div className="app-form-group">
                    <label style={{display: "block", marginBottom: "6px"}}>
                        <input type="radio" name="qr-page" value="1" checked={this.state.qrPage === "1"} onChange={this.changeQRPage}/> Just The First Page
                    </label>
                    <label style={{display: "block"}}>
                        <input type="radio" name="qr-page" value="2" checked={this.state.qrPage === "2"} onChange={this.changeQRPage}/> All Pages
                    </label>
                </div>
            </div>
        );
    }

    renderStep3() {
        const invoice = this.state.selectedQuickBooksInvoiceId;
        return (
            <div>
                <h2>Review & generate</h2>
                <p style={{color: "#8892a0", marginTop: "-8px"}}>Double-check your choices, preview the result, then generate the QR invoice.</p>

                <div className="app-card" style={{background: "#fafbfc"}}>
                    <div className="app-card-title">Your Selections</div>
                    {[
                        ["Invoice", invoice ? invoice.label : "Not selected yet"],
                        ["QR Type", QR_TYPE_LABELS[this.state.qrType]],
                        ["Add QR To", this.state.qrPage === "1" ? "Just The First Page" : "All Pages"],
                    ].map(([label, value]) => (
                        <div key={label} style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", padding: "12px 0", borderBottom: "1px solid #e6e9ed"}}>
                            <span style={{color: "#8892a0", flexShrink: 0}}>{label}</span>
                            <span style={{fontWeight: 700, textAlign: "right"}}>{value}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    renderStep4() {
        const invoice = this.state.selectedQuickBooksInvoiceId;
        return (
            <div>
                <h2>Your QR invoice is ready!</h2>
                <p style={{color: "#8892a0", marginTop: "-8px"}}>It's ready to download, print, or send{this.state.userSettings.company_name ? " to " + this.state.userSettings.company_name + "'s customer" : ""}.</p>

                <div style={{background: "#eef1f7", borderRadius: "12px", padding: "20px 24px", marginBottom: "24px"}}>
                    <div style={{display: "flex", justifyContent: "space-between", padding: "6px 0"}}>
                        <span style={{color: "#8892a0"}}>Invoice</span>
                        <span style={{fontWeight: 700}}>{invoice ? invoice.label : ""}</span>
                    </div>
                    <div style={{display: "flex", justifyContent: "space-between", padding: "6px 0", marginBottom: "16px"}}>
                        <span style={{color: "#8892a0"}}>Amount</span>
                        <span style={{fontWeight: 700}}>{this.state.userCurrency} {invoice ? invoice.totalAmount : ""}</span>
                    </div>
                    <label className="app-form-label">Email to client:</label>
                    <div style={{display: "flex", gap: "8px"}}>
                        <input className={"app-form-input" + (this.state.messageRecipientIsInvalid ? " invalid" : "")} name="messageRecipient"
                               placeholder="billing@client.com" onChange={this.handleChange} value={this.state.messageRecipient} style={{background: "#fff"}} />
                        <button className="btn btn-outline" style={{background: "#fff"}} disabled={this.state.sendInvoiceButtonLoading} onClick={this.sendEmail}>Send</button>
                    </div>
                    {this.state.messageRecipientIsInvalid ? <span className="app-form-error">Invalid recipient address</span> : null}
                </div>

                <div style={{display: "flex", alignItems: "center", gap: "20px"}}>
                    <button className="btn" style={{background: "#1a1f29", color: "#fff"}} disabled={this.state.downloadGeneratedQrButtonLoading}
                            onClick={() => this.downloadGeneratedQrInvoice(this.state.resultQrInvoiceId, invoice ? invoice.label : "invoice")}>
                        Download PDF
                    </button>
                    <a href="javascript:void(0)" onClick={this.startAnother}>Create Another</a>
                </div>
            </div>
        );
    }

    render() {
        if (this.state.isPageLoading) {
            return <LoadingScreen />;
        }

        // A failed payment freezes the company: no new QR invoices until Paddle charges it again (the server refuses the
        // preview and the generation too). Reached by opening /new-qr directly, since every New QR button is disabled.
        if (this.state.userSettings.is_active === false) {
            return (
                <div>
                    <a href="javascript:void(0)" onClick={() => this.props.history.push('/all-invoices')}
                       style={{display: "inline-flex", alignItems: "center", gap: "4px", color: "#8892a0", fontSize: "0.85rem", marginBottom: "8px"}}>
                        <ChevronLeftIcon size={14} /> All QR Invoices
                    </a>
                    <h2 style={{marginBottom: "24px"}}>New QR Invoice</h2>
                    <FrozenSubscriptionBanner userSettings={this.state.userSettings} />
                </div>
            );
        }

        const showPreview = this.state.currentStep < 4;
        return (
            <div>
                <a href="javascript:void(0)" onClick={() => this.props.history.push('/all-invoices')}
                   style={{display: "inline-flex", alignItems: "center", gap: "4px", color: "#8892a0", fontSize: "0.85rem", marginBottom: "8px"}}>
                    <ChevronLeftIcon size={14} /> All QR Invoices
                </a>
                <h2 style={{marginBottom: "24px"}}>New QR Invoice</h2>
                {this.state.currentStep <= 3 ? this.renderStepper() : null}

                <div className="app-grid-row">
                    <div className="app-grid-col" style={{flex: showPreview ? "1 1 500px" : "1 1 100%"}}>
                        <div className="app-card">
                            {this.state.currentStep === 1 ? this.renderStep1() : null}
                            {this.state.currentStep === 2 ? this.renderStep2() : null}
                            {this.state.currentStep === 3 ? this.renderStep3() : null}
                            {this.state.currentStep === 4 ? this.renderStep4() : null}
                            {this.state.premiumEmail ? (
                                <PremiumFeatureModal userSettings={this.state.userSettings} title={EMAIL_PREMIUM_TITLE} message={EMAIL_PREMIUM_MESSAGE}
                                                     onClose={() => this.setState({premiumEmail: false})}
                                                     onUpgraded={(plan) => this.emailAfterUpgrade(plan)} />
                            ) : null}
                            {/* Asked as soon as the QR invoice is ready; keyed by the invoice so each new one asks again */}
                            {this.state.currentStep === 4 && isEligibleForReviewPrompt(this.state.userSettings) ? (
                                <ReviewPromptModal source="qr_ready" key={this.state.resultQrInvoiceId || "result"} realmId={this.state.userSettings.active_realm_id} />
                            ) : null}

                            {this.state.currentStep <= 3 ? (
                                <div style={{display: "flex", justifyContent: "space-between", marginTop: "24px"}}>
                                    {/* No Back on the first step; the empty span keeps Next right-aligned (space-between). */}
                                    {this.state.currentStep === 1 ? <span /> : <button className="btn btn-outline" onClick={this.goBack}>Back</button>}
                                    {this.state.currentStep < 3 ? (
                                        <button className="btn btn-primary" disabled={this.state.nextButtonLoading} onClick={this.goNext}>
                                            {this.state.nextButtonLoading ? (
                                                <span style={{
                                                    display: "inline-block", width: "14px", height: "14px",
                                                    border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff",
                                                    borderRadius: "50%", animation: "app-spin 0.8s linear infinite",
                                                }} />
                                            ) : null}
                                            {this.state.nextButtonLoading ? "Updating…" : "Next"}
                                        </button>
                                    ) : (
                                        <button className="btn btn-primary" disabled={this.state.goButtonLoading} onClick={this.processPDF}>
                                            {this.state.goButtonLoading ? (
                                                <span style={{
                                                    display: "inline-block", width: "14px", height: "14px",
                                                    border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff",
                                                    borderRadius: "50%", animation: "app-spin 0.8s linear infinite",
                                                }} />
                                            ) : null}
                                            {this.state.goButtonLoading ? "Generating…" : "Generate QR Invoice"}
                                        </button>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    {showPreview ? (
                        <div className="app-grid-col app-grid-col-preview" style={{flex: "1 1 320px"}}>
                            <div className="app-card" style={{position: "relative"}}>
                                <div className="app-card-title">Result Preview (First Page)</div>
                                {this.state.selectedQuickBooksInvoiceId === null ? (
                                    <div style={{textAlign: "center", color: "#8892a0", padding: "60px 0"}}>
                                        <div style={{display: "flex", justifyContent: "center", marginBottom: "8px"}}><GridIcon size={32} /></div>
                                        No PDF file specified.
                                    </div>
                                ) : (
                                    <React.Fragment>
                                        {(() => {
                                            // The QR must never be on screen before the invoice it belongs on: wait until
                                            // the invoice has painted AND the QR thumbnail is ready (or cutting it out failed).
                                            const invoiceShown = this.state.visiblePaintedFile === this.getVisiblePreviewFile();
                                            const canDrag = this.state.currentStep === 2 && invoiceShown && (!!this.state.qrThumbnailUrl || this.state.qrThumbnailFailed);
                                            if (!canDrag) {
                                                return null;
                                            }

                                            // A transparent grab handle only - the actual QR shown underneath comes
                                            // from the real generated preview PDF (fetchQrPositionPreview). Sized
                                            // and positioned from the exact box the backend drew the QR into
                                            // (qrBoxPts, read off the preview response's headers) so it hugs the
                                            // real QR pixel-for-pixel regardless of how big the QR itself renders.
                                            const scale = this.state.pdfScale || 1;
                                            const box = this.state.qrBoxPts;
                                            const offsetLeft = this.state.previewOffsetLeft;
                                            const offsetTop = this.state.previewOffsetTop;
                                            const dragStyle = {
                                                cursor: this.state.isDraggingQr ? "grabbing" : "grab",
                                                outline: "2px dashed #224514", outlineOffset: "2px",
                                                zIndex: 1, position: "absolute",
                                            };
                                            // "You can drag this": four arrows push out from the QR's sides and fade, for
                                            // 3s (CSS only). They play once each time step 2 opens (qrDragHintPlayed, so a
                                            // re-mounted handle doesn't replay them), and never block the drag (pointer-events: none).
                                            const dragHint = this.state.qrDragHintPlayed ? null : (
                                                <span className="qr-drag-hint" aria-hidden="true"
                                                      onAnimationEnd={() => this.setState({qrDragHintPlayed: true})}>
                                                    {["up", "right", "down", "left"].map((side) => (
                                                        <span key={side} className={"qr-drag-hint-arrow qr-drag-hint-" + side}>
                                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="#224514"
                                                                 strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M6 15l6-6 6 6" />
                                                            </svg>
                                                        </span>
                                                    ))}
                                                </span>
                                            );

                                            if (box) {
                                                // box.x/box.y are frozen at wherever they were when last fetched
                                                // from the server (baseCustomX/Y) - dragging no longer re-fetches
                                                // on every frame (that network round-trip was the "jerky drag"
                                                // bug), so the handle must track the cursor by ALGEBRA instead:
                                                // shift the frozen box by exactly how far customPosition has
                                                // moved since that fetch. This is the same relationship the
                                                // backend uses (increasing customX moves the box left, increasing
                                                // customY moves it down from the top-right anchor), just solved in the
                                                // opposite direction.
                                                const dx = this.state.customPositionX - box.baseCustomX;
                                                const dy = this.state.customPositionY - box.baseCustomY;
                                                const liveX = box.x - dx;
                                                const liveY = box.y - dy;
                                                return (
                                                    <div ref={this.dragHandleRef} onMouseDown={this.handleQrDragStart} style={{
                                                        ...dragStyle,
                                                        left: (offsetLeft + liveX * scale) + "px",
                                                        top: (offsetTop + (box.pageHeight - liveY - box.size) * scale) + "px",
                                                        width: (box.size * scale) + "px",
                                                        height: (box.size * scale) + "px",
                                                    }}>
                                                        {this.state.qrThumbnailUrl ? (
                                                            <img src={this.state.qrThumbnailUrl} alt="" draggable={false}
                                                                 style={{width: "100%", height: "100%", display: "block", pointerEvents: "none"}} />
                                                        ) : null}
                                                        {dragHint}
                                                    </div>
                                                );
                                            }

                                            // Fallback approximate placement while the first real preview is still loading.
                                            const positionXPx = this.state.customPositionX * scale;
                                            const positionYPx = this.state.customPositionY * scale;
                                            const fallbackStyle = {...dragStyle, width: QR_PREVIEW_ICON_PX + "px", height: QR_PREVIEW_ICON_PX + "px", top: (offsetTop + positionYPx) + "px"};
                                            return (
                                                <div ref={this.dragHandleRef} onMouseDown={this.handleQrDragStart}
                                                     style={{...fallbackStyle, left: (offsetLeft + this.state.previewRenderWidth - QR_PREVIEW_ICON_PX - positionXPx) + "px"}}>
                                                    {dragHint}
                                                </div>
                                            );
                                        })()}
                                        {this.state.qrPreviewRendering ? (
                                            // Blurs the invoice with a centered spinner while the server re-renders the preview.
                                            // Placed with the same measured offsets the drag handle uses: wrapping the preview in a
                                            // position:relative box would change its offsetParent and break the drag math. Sits above
                                            // the drag handle (zIndex 2 > 1), so the QR can't be dragged mid-update.
                                            <div aria-label="Updating preview" role="status"
                                                 style={{position: "absolute", zIndex: 2,
                                                         top: this.state.previewOffsetTop + "px", left: this.state.previewOffsetLeft + "px",
                                                         width: this.state.previewRenderWidth + "px",
                                                         height: (this.previewDivRef.current ? this.previewDivRef.current.offsetHeight : 0) + "px",
                                                         display: "flex", alignItems: "center", justifyContent: "center",
                                                         background: "rgba(255, 255, 255, 0.35)", backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)"}}>
                                                <div style={{width: "36px", height: "36px", borderRadius: "50%",
                                                             border: "3px solid #dce7d6", borderTopColor: "#224514",
                                                             animation: "app-spin 0.8s linear infinite"}} />
                                            </div>
                                        ) : null}
                                        <div className="qr-drag-preview" ref={this.setPreviewDivRef} style={{width: "100%"}}>
                                            {/* Step 2 shows the PLAIN invoice: the QR is only the draggable thumbnail, so the spot
                                                it started on shows the real invoice (e.g. a logo) once the QR is dragged away. */}
                                            <Document file={this.getVisiblePreviewFile()} options={SAFE_PDF_OPTIONS} loading={<PdfLoadingSpinner />}>
                                                <PDFPage pageNumber={1} renderTextLayer={false} renderAnnotationLayer={false} width={this.state.previewRenderWidth} onLoadSuccess={this.onPdfPageLoadSuccess} onRenderSuccess={this.onVisiblePageRendered} />
                                            </Document>
                                        </div>
                                        {this.state.currentStep === 2 && this.state.qrRenderedPreviewUrl ? (
                                            // Off-screen copy of the QR-stamped preview, only used to cut the QR out as the thumbnail.
                                            <div ref={this.hiddenQrPreviewRef} aria-hidden="true"
                                                 style={{position: "absolute", left: "-10000px", top: 0, width: this.state.previewRenderWidth + "px", pointerEvents: "none"}}>
                                                <Document file={this.state.qrRenderedPreviewUrl} options={SAFE_PDF_OPTIONS} loading={null}>
                                                    <PDFPage pageNumber={1} renderTextLayer={false} renderAnnotationLayer={false} width={this.state.previewRenderWidth} onRenderSuccess={this.captureQrThumbnail} />
                                                </Document>
                                            </div>
                                        ) : null}
                                    </React.Fragment>
                                )}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }
}

export default withRouter(NewQR);
